import constate from 'constate';
import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Context hook to keep track of the previous visited page and use it as the referrer.
 *
 * <p>This is used to know how the users are navigating between the SPA pages. The first referrer
 * is actually taken from {@code document.referrer}, but subsequent ones are the internal paths used
 * in the app.
 */
function useReferrer(): React.MutableRefObject<string> {
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;
  // We start the "current" path as the external referrer as it will be pushed to "previous" right
  // after rendering because of `useLayoutEffect`. This is to ensure `useEffect` hooks relying on
  // this value always see correct values (i.e., this hook runs before them).
  const currentPathRef = useRef<string>(document.referrer);
  const previousPathRef = useRef<string>('');
  /*
   * Reviewed by UX Programs (Jaz) as a valid `useEffect` or `useLayoutEffect` usage.
   * See https://getcolor.atlassian.net/browse/UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useLayoutEffect(() => {
    previousPathRef.current = currentPathRef.current;
    currentPathRef.current = path;
  }, [path]);

  return previousPathRef;
}

const dest = constate(useReferrer);
export const ReferrerProvider: React.FC<React.PropsWithChildren<object>> = dest[0];
export const useReferrerContext: () => React.MutableRefObject<string> = dest[1];
